import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  ArrowRight, 
  Calendar,
  Sparkles,
  Shield,
  Zap,
  Target,
  PenTool,
  Users,
  BarChart3,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Video,
  TrendingUp,
  Search,
  Globe,
  Megaphone,
  FileText,
  Eye,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { cubeLogo, trustedBrands, landscapeVideos, shortsVideos } from "../data/mock";
import { Play } from "lucide-react";

// Social Platform Logos as SVG components
const FacebookLogo = () => (
  <svg viewBox="0 0 24 24" className="w-8 h-8" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramLogo = () => (
  <svg viewBox="0 0 24 24" className="w-8 h-8">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FFDC80"/>
        <stop offset="25%" stopColor="#F77737"/>
        <stop offset="50%" stopColor="#E1306C"/>
        <stop offset="75%" stopColor="#C13584"/>
        <stop offset="100%" stopColor="#833AB4"/>
      </linearGradient>
    </defs>
    <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeLogo = () => (
  <svg viewBox="0 0 24 24" className="w-8 h-8" fill="#FF0000">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

// Video Testimonial Component
const VideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="relative aspect-video overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative aspect-video overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-red-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20" />
      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-16 h-16 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-7 h-7 text-white fill-white ml-1" />
        </motion.div>
      </div>
      {/* Title at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-white font-medium text-sm line-clamp-2">{video.title}</p>
      </div>
    </motion.div>
  );
};

// Shorts Video Testimonial Component (Portrait)
const ShortsVideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/0.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.05 }}
        className="relative aspect-[9/16] overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative aspect-[9/16] overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-red-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      {/* Title and industry at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white font-medium text-xs line-clamp-2">{video.title}</p>
        <p className="text-red-400 text-[10px] mt-1">{video.industry}</p>
      </div>
    </motion.div>
  );
};

// Main Plans Data
const mainPlans = [
  {
    id: "social-media-quarterly",
    name: "Social Media Management",
    subtitle: "Quarterly Package",
    price: "35,000",
    originalPrice: "45,000",
    gstPrice: "41,300",
    duration: "3 Months",
    durationHighlight: "3 MONTHS",
    billing: "One-Time Payment",
    discount: "₹10,000 OFF!",
    recommended: true,
    idealFor: "Startups, creators, brands looking for consistency on Facebook & Instagram",
    features: [
      "45+ Engaging Contents (Graphic + Reels)",
      "Alt Day Posting Schedule",
      "60+ Stories (Festival, BTS, Offers, Polls)",
      "Reels Creation Support (Client-shot)",
      "Social Media Strategy + Monthly Planning",
      "Dedicated Social Media Manager",
      "Brand Bio, CTA Button & Highlight Setup",
      "Captions + Hashtag Strategy",
      "Monthly Growth & Analytics Reports",
      "1 Free Ad Campaign Setup",
      "Audience Engagement (DMs, replies, polls)"
    ],
    color: "red"
  },
  {
    id: "growth-subscription",
    name: "Digital Marketing Growth",
    subtitle: "Monthly Subscription",
    price: "14,999",
    gstPrice: "17,699",
    duration: "Per Month",
    durationHighlight: "1 MONTH",
    billing: "Billed Quarterly",
    recommended: false,
    idealFor: "Businesses seeking flexible, results-focused digital presence",
    features: [
      "Facebook & Instagram Management",
      "Monthly Growth Strategies",
      "Flexible Posting (No fixed limits)",
      "Audience Engagement & Interactions",
      "Monthly Content Calendar",
      "High-Quality Social Media Designs",
      "4-Hour Video Shoot Session/Month",
      "Reel Guidance, Scripting & Editing",
      "2 Bonus Creative Concept Reels/Month",
      "Expert Support Team Access",
      "Google Business Profile Optimization"
    ],
    extras: [
      "Account Manager",
      "Social Media Executive", 
      "Graphic Designer",
      "Video Editor",
      "Content Writer"
    ],
    color: "gray"
  }
];

// Facebook/Meta Logo SVG
const FacebookAdsLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Google Logo SVG
const GoogleAdsLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Ads Management Plans
const adsPlans = [
  {
    id: "facebook-ads-full",
    name: "Facebook Ads Full Funnel",
    subtitle: "Performance Marketing",
    price: "9,999",
    originalPrice: "14,999",
    gstPrice: "11,799",
    duration: "Per Month",
    recommended: true,
    features: [
      "Facebook & Instagram Campaigns",
      "Traffic, Sales & Lead Generation",
      "Complete Funnel Setup",
      "Ad Scripts & Video Concepts",
      "Edit Support for Creatives",
      "A/B Testing & Optimization",
      "Retargeting + Custom Audiences",
      "Weekly Optimization",
      "Live Dashboard Access",
      "Monthly Strategy Call + Report"
    ],
    logoType: "facebook",
    color: "blue"
  },
  {
    id: "google-ads",
    name: "Google Ads Management",
    subtitle: "Search & Display",
    price: "14,999",
    originalPrice: "19,999",
    gstPrice: "17,699",
    duration: "Per Month",
    recommended: false,
    features: [
      "Google Search Ads",
      "Display Network Ads",
      "YouTube Video Ads",
      "Keyword Research & Ad Copywriting",
      "Ad Extensions Setup",
      "Negative Keywords Management",
      "Conversion Tracking",
      "Budget Optimization",
      "GMB Integration + Local Ads",
      "Weekly Report + Call"
    ],
    logoType: "google",
    color: "multi"
  }
];

// Super Ads Plans
const superAdsPlans = [
  {
    id: "starter",
    name: "Starter",
    price: "4,499",
    duration: "10 Days",
    views: "45,000+",
    leads: "75-90",
    recommended: false,
    features: [
      "Ad Design Included",
      "Facebook & Instagram Ads",
      "Budget Allocation",
      "Campaign Setup",
      "Basic Reporting",
    ]
  },
  {
    id: "business",
    name: "Business",
    price: "8,499",
    duration: "20 Days",
    views: "75,000+",
    leads: "125-180",
    recommended: true,
    features: [
      "Ad Design Included",
      "Facebook & Instagram Ads",
      "Budget Allocation",
      "Campaign Setup",
      "Performance Optimization",
      "Weekly Reporting",
    ]
  },
  {
    id: "grow",
    name: "Grow",
    price: "13,499",
    duration: "30 Days",
    views: "96,000+",
    leads: "190-260",
    recommended: false,
    features: [
      "Premium Ad Design",
      "Facebook & Instagram Ads",
      "Advanced Targeting",
      "Campaign Setup & Management",
      "A/B Testing",
      "Daily Optimization",
      "Detailed Analytics Report",
    ]
  }
];

// Addon Plans
const addonPlans = [
  {
    id: "facebook-performance",
    name: "Facebook Performance Ads",
    price: "5,999",
    originalPrice: "9,999",
    duration: "/month + GST",
    features: [
      "Campaign Setup: Sales, Leads, WhatsApp, Follower Growth",
      "Ad Script & Caption Writing",
      "Ad Creative Planning (client-shot)",
      "Campaign Execution & Optimization",
      "Retargeting + Custom Audiences",
      "Funnel Strategy Call + Reporting"
    ],
    logoType: "facebook"
  },
  {
    id: "reels-shoot",
    name: "Reels Shoot",
    subtitle: "Studio/Outdoor",
    price: "5,999",
    originalPrice: "9,999",
    duration: "/day + GST",
    features: [
      "4-5 Professional Reels in One Day",
      "Scripting & Direction",
      "Lighting Setup",
      "Raw Footage Delivery"
    ],
    note: "Studio, Editing & model cost extra if required",
    icon: Video
  }
];

// FAQ Data
const faqData = [
  {
    question: "What platforms do you manage?",
    answer: "We primarily manage Facebook and Instagram through Meta's advertising and content management platforms. For Google Ads, we handle Search, Display, and YouTube campaigns."
  },
  {
    question: "What's included in the designing cost?",
    answer: "We create professional ad creatives including images, carousel ads, and video content as needed. The number of creatives depends on your plan - higher plans include more creative variations for A/B testing."
  },
  {
    question: "Is the ad spend included in the pricing?",
    answer: "No, ad spend is separate and will be determined in consultation with our Digital Marketing Manager to align with your objectives. No additional management charge for monthly ad spend up to ₹1 lakh."
  },
  {
    question: "How do I track my campaign performance?",
    answer: "We provide regular reports based on your plan - basic reporting for starter plans, weekly for mid-tier, and detailed daily analytics for premium plans. You'll have full visibility into views, clicks, leads, and ROI."
  },
  {
    question: "Can I customize my plan?",
    answer: "Absolutely! Contact us for a custom quote based on your specific requirements. We can tailor our services to match your business goals and budget."
  },
  {
    question: "What's the minimum commitment period?",
    answer: "Our Social Media Management is a 3-month package. The Growth Subscription is billed quarterly but can be renewed monthly after the first quarter. Ads management is month-to-month with no long-term commitment."
  }
];

const WHATSAPP_URL = "https://wa.me/918777624646?text=Hi%2C%20I%27m%20interested%20in%20your%20Social%20Media%20Marketing%20Plans";
const RAZORPAY_URL = "https://razorpay.me/@cubemultimedia";

export default function SocialMediaPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState("ads");
  const [videoTab, setVideoTab] = useState("landscape");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    window.open(RAZORPAY_URL, "_blank");
  };

  const handleWhatsApp = () => {
    window.open(WHATSAPP_URL, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-12 md:h-16" />
          </Link>
          <Button 
            onClick={handleWhatsApp}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg text-sm font-medium"
            data-testid="header-get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-6">
              <Sparkles className="w-4 h-4" />
              Complete Social Media Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Social Media
              <span className="text-[#dc2626]"> Marketing Plans</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              From content creation to paid advertising — we handle everything to grow your brand's digital presence
            </p>
            
            {/* Platform Logos */}
            <div className="flex flex-wrap justify-center gap-6 items-center">
              <motion.div 
                whileHover={{ scale: 1.1 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/5 rounded-xl border border-white/10 hover:border-blue-500/50 transition-all cursor-pointer"
              >
                <FacebookLogo />
                <span className="font-medium">Facebook</span>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.1 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/5 rounded-xl border border-white/10 hover:border-pink-500/50 transition-all cursor-pointer"
              >
                <InstagramLogo />
                <span className="font-medium">Instagram</span>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.1 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/5 rounded-xl border border-white/10 hover:border-red-500/50 transition-all cursor-pointer"
              >
                <YouTubeLogo />
                <span className="font-medium">YouTube</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="px-4 pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 p-1.5 bg-white/5 rounded-xl border border-white/10">
            {[
              { id: "ads", label: "Ads Management", icon: Target },
              { id: "superads", label: "Super Ads", icon: Zap },
              { id: "management", label: "Social Media Management", icon: Users },
              { id: "addons", label: "Add-ons", icon: Sparkles }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-[#dc2626] text-white"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Management Plans */}
      {activeTab === "management" && (
        <section className="px-4 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Choose Your <span className="text-[#dc2626]">Management Plan</span>
              </h2>
              <p className="text-gray-400">Complete social media handling for your brand</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {mainPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-2xl p-6 lg:p-8 ${
                    plan.recommended
                      ? "bg-gradient-to-b from-red-900/30 to-[#141414] border-2 border-red-500/50"
                      : "bg-[#141414] border border-white/10"
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-[#dc2626] text-white text-xs font-bold rounded-full uppercase">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {plan.discount && (
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
                        {plan.discount}
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                    <p className="text-gray-400 text-sm">{plan.subtitle}</p>
                  </div>

                  {/* Duration Highlight Badge */}
                  <div className="mb-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold ${
                      plan.recommended 
                        ? "bg-gradient-to-r from-red-600 to-orange-500 text-white" 
                        : "bg-gradient-to-r from-blue-600 to-purple-500 text-white"
                    }`}>
                      <Clock className="w-4 h-4" />
                      {plan.durationHighlight}
                    </span>
                  </div>

                  <div className="mb-6">
                    {plan.originalPrice && (
                      <p className="text-gray-500 line-through text-sm">
                        ₹{plan.originalPrice} + GST
                      </p>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">₹{plan.price}</span>
                      <span className="text-gray-400">+ GST</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Pay ₹{plan.gstPrice}/- • {plan.duration}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{plan.billing}</p>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs text-gray-500 mb-4">
                      Ideal for: {plan.idealFor}
                    </p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.extras && (
                    <div className="mb-6 p-4 bg-white/5 rounded-lg">
                      <p className="text-xs text-gray-400 mb-2">Expert Team Included:</p>
                      <div className="flex flex-wrap gap-2">
                        {plan.extras.map((extra, i) => (
                          <span key={i} className="px-2 py-1 bg-white/10 rounded text-xs text-gray-300">
                            {extra}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleGetStarted}
                    className={`w-full py-6 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                      plan.recommended
                        ? "bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    }`}
                    data-testid={`get-started-${plan.id}`}
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ads Management Plans */}
      {activeTab === "ads" && (
        <section className="px-4 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Ads <span className="text-[#dc2626]">Management</span>
              </h2>
              <p className="text-gray-400">Performance-driven advertising campaigns</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {adsPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-2xl p-6 lg:p-8 ${
                    plan.recommended
                      ? "bg-gradient-to-b from-red-900/30 to-[#141414] border-2 border-red-500/50"
                      : "bg-[#141414] border border-white/10"
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-[#dc2626] text-white text-xs font-bold rounded-full uppercase">
                        Recommended
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-6">
                    <div className={`p-3 rounded-xl ${
                      plan.logoType === "facebook" ? "bg-blue-500/20" : "bg-white/10"
                    }`}>
                      {plan.logoType === "facebook" ? <FacebookAdsLogo /> : <GoogleAdsLogo />}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                      <p className="text-gray-400 text-sm">{plan.subtitle}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    {plan.originalPrice && (
                      <p className="text-gray-500 line-through text-sm">
                        ₹{plan.originalPrice}
                      </p>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">₹{plan.price}</span>
                      <span className="text-gray-400">/month + GST</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Pay ₹{plan.gstPrice}/month inclusive GST
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-6">
                    <p className="text-xs text-yellow-400">
                      Note: Ad spend is separate. No additional management charge for ad spend up to ₹1 lakh/month.
                    </p>
                  </div>

                  <Button
                    onClick={handleGetStarted}
                    className={`w-full py-6 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                      plan.recommended
                        ? "bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    }`}
                    data-testid={`get-started-${plan.id}`}
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Super Ads Plans */}
      {activeTab === "superads" && (
        <section className="px-4 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                Super Ads <span className="text-[#dc2626]">Plan</span>
              </h2>
              <p className="text-gray-400">All-inclusive advertising packages with guaranteed results</p>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <PenTool className="w-4 h-4 text-red-400" />
                  <span className="text-sm">Designing Cost Included</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <TrendingUp className="w-4 h-4 text-red-400" />
                  <span className="text-sm">Advertising Cost Included</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                  <Shield className="w-4 h-4 text-red-400" />
                  <span className="text-sm">GST Included</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {superAdsPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-2xl p-6 ${
                    plan.recommended
                      ? "bg-gradient-to-b from-red-900/30 to-[#141414] border-2 border-red-500/50"
                      : "bg-[#141414] border border-white/10"
                  }`}
                  data-testid={`superads-${plan.id}`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-[#dc2626] text-white text-xs font-bold rounded-full uppercase">
                        Recommended
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold">₹{plan.price}</div>
                    <p className="text-gray-400 text-sm mt-1">All Inclusive</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-6 p-3 bg-white/5 rounded-lg">
                    <div className="text-center">
                      <Calendar className="w-4 h-4 mx-auto text-red-400 mb-1" />
                      <p className="text-xs text-gray-400">Duration</p>
                      <p className="text-sm font-semibold">{plan.duration}</p>
                    </div>
                    <div className="text-center">
                      <Eye className="w-4 h-4 mx-auto text-red-400 mb-1" />
                      <p className="text-xs text-gray-400">Views</p>
                      <p className="text-sm font-semibold">{plan.views}</p>
                    </div>
                    <div className="text-center">
                      <Users className="w-4 h-4 mx-auto text-red-400 mb-1" />
                      <p className="text-xs text-gray-400">Leads</p>
                      <p className="text-sm font-semibold">{plan.leads}</p>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={handleGetStarted}
                    className={`w-full py-5 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                      plan.recommended
                        ? "bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    }`}
                    data-testid={`get-started-superads-${plan.id}`}
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Views and Leads Note */}
            <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-center">
              <p className="text-sm text-gray-400">
                * Views and leads are estimated based on our experience. Actual results may vary depending on industry and target audience.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Addon Plans */}
      {activeTab === "addons" && (
        <section className="px-4 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                <span className="text-[#dc2626]">Add-ons</span>
              </h2>
              <p className="text-gray-400">Enhance your marketing with these additional services</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {addonPlans.map((addon, index) => (
                <motion.div
                  key={addon.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#141414] border border-white/10 rounded-2xl p-6"
                  data-testid={`addon-${addon.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-xl ${addon.logoType === "facebook" ? "bg-blue-500/20" : "bg-red-500/20"}`}>
                      {addon.logoType === "facebook" ? <FacebookAdsLogo /> : <addon.icon className="w-6 h-6 text-red-400" />}
                    </div>
                    <div>
                      <h3 className="font-bold">{addon.name}</h3>
                      {addon.subtitle && (
                        <p className="text-gray-400 text-sm">{addon.subtitle}</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-gray-500 line-through text-sm">₹{addon.originalPrice}</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">₹{addon.price}</span>
                      <span className="text-gray-400 text-sm">{addon.duration}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-4">
                    {addon.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {addon.note && (
                    <p className="text-xs text-yellow-400 mb-4 p-2 bg-yellow-500/10 rounded">
                      Note: {addon.note}
                    </p>
                  )}

                  <Button
                    onClick={handleGetStarted}
                    className="w-full py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-medium"
                    data-testid={`get-started-${addon.id}`}
                  >
                    Add to Plan
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What's Included Section */}
      <section className="px-4 py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              What's <span className="text-[#dc2626]">Included</span>
            </h2>
            <p className="text-gray-400">Everything you need for social media success</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: PenTool, title: "Designing", desc: "Professional ad creatives & posts" },
              { icon: FileText, title: "Content Strategy", desc: "Monthly planning & calendars" },
              { icon: BarChart3, title: "Analytics", desc: "Performance reports & insights" },
              { icon: Shield, title: "GST Included", desc: "No hidden charges" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-[#141414] rounded-xl border border-white/5"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Brands Section */}
      <section className="px-4 py-16 bg-[#0d0d0d] overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Trusted by <span className="text-[#dc2626]">India's Top Brands</span>
            </h2>
            <p className="text-gray-400">We've helped businesses across industries grow their digital presence</p>
          </div>

          {/* Scrolling Brand Logos */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0d0d0d] to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0d0d0d] to-transparent z-10" />
            
            <div className="flex gap-8 animate-scroll">
              {[...trustedBrands, ...trustedBrands].map((brand, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 h-16 w-32 flex items-center justify-center bg-white/5 rounded-xl p-3 border border-white/5"
                >
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="max-h-full max-w-full object-contain filter brightness-90 hover:brightness-110 transition-all"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Video Testimonials Section */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              What Our <span className="text-[#dc2626]">Clients Say</span>
            </h2>
            <p className="text-gray-400">Real video feedback from businesses we've helped grow</p>
          </div>

          {/* Video Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            <button
              onClick={() => setVideoTab("landscape")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "landscape"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="video-tab-landscape"
            >
              Video Feedback
            </button>
            <button
              onClick={() => setVideoTab("shorts")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "shorts"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="video-tab-shorts"
            >
              Shorts
            </button>
          </div>

          {/* Landscape Videos */}
          {videoTab === "landscape" && (
            <div className="grid md:grid-cols-2 gap-6">
              {landscapeVideos.filter(v => v.type === "feedback").map((video, index) => (
                <VideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}

          {/* Shorts Videos */}
          {videoTab === "shorts" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {shortsVideos.slice(0, 10).map((video, index) => (
                <ShortsVideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Frequently Asked <span className="text-[#dc2626]">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#141414] rounded-xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                  data-testid={`faq-${index}`}
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-red-900/30 to-red-800/20 rounded-2xl p-8 md:p-12 border border-red-500/20"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to Grow Your Brand?
            </h2>
            <p className="text-gray-400 mb-8">
              Get in touch with us to discuss your social media marketing needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 rounded-xl font-semibold flex items-center justify-center gap-2"
                data-testid="cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp
              </Button>
              <Button
                onClick={handleGetStarted}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 rounded-xl font-semibold flex items-center justify-center gap-2"
                data-testid="cta-get-started"
              >
                Pay & Get Started <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src={cubeLogo} alt="Cube Multimedia" className="h-10" />
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link>
            </div>
          </div>
          <div className="mt-4 text-center text-xs text-gray-500">
            Effective from 1 April 2026 • www.cubemultimedia.in
          </div>
        </div>
      </footer>
    </div>
  );
}
